import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent, IconButton, makeStyles, Tooltip } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import FullscreenIcon from '@material-ui/icons/Fullscreen'

const useStyles = makeStyles(theme => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  toolbar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  modeToggle: {
    display: 'flex',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden'
  },
  modeBtn: {
    padding: '4px 10px',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-600)',
    backgroundColor: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:not(:last-child)': { borderRight: '1px solid var(--color-neutral-300)' },
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed' }
  },
  modeBtnActive: {
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
    '&:hover': { backgroundColor: 'var(--color-primary-600)' }
  },
  spacer: { flex: 1 },
  iconBtn: {
    padding: 4,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-primary-600)' },
    '&:disabled': { color: 'var(--color-neutral-200)' }
  },
  body: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    backgroundColor: 'white'
  },
  json: {
    margin: 0,
    padding: theme.spacing(1.5),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    color: 'var(--color-neutral-800)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  },
  errorBox: {
    padding: theme.spacing(1.5),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    color: 'var(--color-danger-700, #b91c1c)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '11px',
    fontFamily: 'var(--font-mono, monospace)'
  },
  th: {
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    position: 'sticky',
    top: 0
  },
  td: {
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid var(--color-neutral-100)',
    color: 'var(--color-neutral-800)',
    verticalAlign: 'top',
    whiteSpace: 'nowrap'
  },
  modalPaper: {
    width: '90vw',
    height: '85vh',
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 'var(--border-radius-md, 8px)',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  modalTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600
  },
  modalContent: {
    flex: 1,
    overflow: 'hidden',
    padding: 0,
    display: 'flex',
    flexDirection: 'column'
  }
}))

function isTableLike (data) {
  if (!Array.isArray(data) || data.length === 0) return false
  return data.every(row => row && typeof row === 'object' && !Array.isArray(row))
}

function flattenForTable (data) {
  const cols = []
  const seen = new Set()
  for (const row of data) {
    for (const k of Object.keys(row || {})) {
      if (!seen.has(k)) { seen.add(k); cols.push(k) }
    }
  }
  return cols
}

function renderCell (val) {
  if (val == null) return ''
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

function ResultsBody ({ result, error, mode }) {
  const classes = useStyles()
  if (error) return <div className={classes.errorBox}>{error}</div>
  if (result == null) {
    return <div className={classes.empty}>No results yet. Click Run to execute the pipeline.</div>
  }
  if (mode === 'table' && isTableLike(result)) {
    const cols = flattenForTable(result)
    return (
      <table className={classes.table}>
        <thead>
          <tr>{cols.map(c => <th key={c} className={classes.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {result.map((row, idx) => (
            <tr key={idx}>
              {cols.map(c => <td key={c} className={classes.td}>{renderCell(row[c])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }
  return <pre className={classes.json}>{JSON.stringify(result, null, 2)}</pre>
}

ResultsBody.propTypes = {
  result: PropTypes.any,
  error: PropTypes.string,
  mode: PropTypes.oneOf(['json', 'table']).isRequired
}

function ResultsToolbar ({ mode, setMode, tableSupported, onPopout, popoutDisabled }) {
  const classes = useStyles()
  return (
    <div className={classes.toolbar}>
      <div className={classes.modeToggle}>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'json' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('json')}
        >
          json
        </button>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'table' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('table')}
          disabled={!tableSupported}
        >
          table
        </button>
      </div>
      <div className={classes.spacer} />
      {onPopout && (
        <Tooltip title='Open in modal' arrow>
          <span>
            <IconButton size='small' className={classes.iconBtn} onClick={onPopout} disabled={popoutDisabled}>
              <FullscreenIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  )
}

ResultsToolbar.propTypes = {
  mode: PropTypes.oneOf(['json', 'table']).isRequired,
  setMode: PropTypes.func.isRequired,
  tableSupported: PropTypes.bool.isRequired,
  onPopout: PropTypes.func,
  popoutDisabled: PropTypes.bool
}

function ResultsView ({ result, error }) {
  const classes = useStyles()
  const [mode, setMode] = useState('json')
  const [popped, setPopped] = useState(false)

  const tableSupported = useMemo(() => isTableLike(result), [result])

  return (
    <div className={classes.wrap}>
      <ResultsToolbar
        mode={mode}
        setMode={setMode}
        tableSupported={tableSupported}
        onPopout={() => setPopped(true)}
        popoutDisabled={result == null && !error}
      />
      <div className={classes.body}>
        <ResultsBody result={result} error={error} mode={mode} />
      </div>
      <Dialog open={popped} onClose={() => setPopped(false)} classes={{ paper: classes.modalPaper }} maxWidth={false}>
        <div className={classes.modalHeader}>
          <span className={classes.modalTitle}>Pipeline Result</span>
          <IconButton size='small' onClick={() => setPopped(false)}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
        <DialogContent className={classes.modalContent}>
          <ResultsToolbar mode={mode} setMode={setMode} tableSupported={tableSupported} />
          <div className={classes.body}>
            <ResultsBody result={result} error={error} mode={mode} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

ResultsView.propTypes = {
  result: PropTypes.any,
  error: PropTypes.string
}

export default ResultsView
